import { safeNavItems } from './navItemUtils';

/**
 * True when an item is a plain link: not an embedded fragment and with no
 * children of its own.
 *
 * @param {NavigationItem} item
 * @returns {boolean}
 */
const isFlatLink = (item) => !item.embedded && safeNavItems(item).length === 0;

/**
 * Classifies a mega menu column item for render dispatch. Adding a new kind of
 * navigation item means adding a case here and a matching small component —
 * callers switch on the returned tag instead of growing an if/else chain.
 *
 * @param {NavigationItem} item
 * @returns {'fragment' | 'group' | 'link'}
 */
export const classifyMegaMenuItem = (item) => {
	if (item?.embedded) return 'fragment';
	if (safeNavItems(item).length > 0) return 'group';
	return 'link';
};

/**
 * True when a group's children are all plain links. A group's children can be
 * spread into a 3-column layout only when every child is a plain link — an
 * embedded fragment (e.g. a body-style facet widget) owns its own internal
 * layout and must stay in a single track.
 *
 * @param {NavigationItem} item
 * @returns {boolean}
 */
export const hasOnlyLinkChildren = (item) => {
	const children = safeNavItems(item);
	return children.length > 0 && children.every((child) => !child.embedded);
};

/**
 * Resolves the heading text a column item should display: the item's own label
 * if it has one, otherwise the parent featured item's own label. This is the
 * single place "what heading does this show" is decided, so every column always
 * renders a heading instead of leaving an unexplained blank gap.
 *
 * Returns undefined only in the rare, defensive case where neither the item nor
 * the parent has a usable label — there is nothing left to fall back to, so no
 * heading renders.
 *
 * @param {NavigationItem} item
 * @param {string} [parentLabel] - Label of the featured item this column belongs to.
 * @returns {string | undefined}
 */
export const resolveHeadingLabel = (item, parentLabel) =>
	item?.label?.trim() || parentLabel?.trim() || undefined;

/**
 * Shapes a column's raw navigation items for rendering.
 *
 * A column whose entire content is flat links (any count — a single lone link
 * included) has no group of its own to supply a heading, so it is wrapped into
 * one synthesized group. The synthesized group's label is left unset on purpose:
 * resolveHeadingLabel supplies the parent label for it exactly like any other
 * headingless group, so there is a single heading-resolution code path.
 *
 * Columns that already contain a real group or an embedded fragment pass through
 * unchanged.
 *
 * @param {NavigationColumn} column
 * @returns {NavigationItem[]}
 */
export const shapeColumnItems = (column) => {
	const rawItems = safeNavItems(column);
	const allFlatLinks = rawItems.length > 0 && rawItems.every(isFlatLink);
	if (!allFlatLinks) return rawItems;
	return [
		{
			id: `${column.gridUnits}-fallback-heading`,
			label: undefined,
			navigationItems: rawItems
		}
	];
};

/**
 * gridUnits value of a column that has no sibling columns — the nav-service
 * emits this whenever a featured item has no `layout` split, meaning the
 * column owns the entire mega-menu width (see src/types.js's gridUnits doc:
 * a 12-unit grid). Sibling-split columns (e.g. a 4:4:4 layout) never reach 12.
 */
const FULL_WIDTH_GRID_UNITS = 12;

/**
 * True when a column's sole content is a single group of plain links that
 * should spread into the full-width 3-column grid.
 *
 * Only eligible when the column has no sibling columns (gridUnits === 12) —
 * spreading a sibling column's own flat links into a further 3-way split
 * squeezes them into a fraction of a fraction of the mega menu's width,
 * which is what wraps link text into a jumbled multi-line mess. A full-width
 * column has the whole layout to itself, so any number of links (not just a
 * handful) can spread there safely.
 *
 * Requires more than one link to spread: a lone link would only shrink to 1/3
 * width in a 3-column grid for no reason, so wrapping it in a heading-group must
 * not also force it into the grid layout.
 *
 * @param {NavigationItem[]} items - Shaped column items.
 * @param {number} gridUnits - The column's own gridUnits (out of 12).
 * @returns {boolean}
 */
export const isColumnFullWidth = (items, gridUnits) => {
	if (gridUnits !== FULL_WIDTH_GRID_UNITS) return false;
	if (items.length !== 1) return false;
	const [only] = items;
	if (!hasOnlyLinkChildren(only)) return false;
	return safeNavItems(only).length > 1;
};

/**
 * Skeleton placeholder height for embedded fragments, proportional to the
 * column's grid width so a wider column reserves more vertical space.
 *
 * @param {NavigationColumn} column
 * @returns {string}
 */
export const columnSkeletonHeight = (column) => `${column.gridUnits * 2}rem`;
